import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import styled from 'styled-components';
import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import type { FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import TsxCheckbox from '@/app/common/filters/types/shared/TsxCheckbox.tsx';
import type { CustomOptionsCreators } from '@/app/common/filters/types/shared/Custom/types.tsx';
import { getColumnRows } from '@/app/common/filters/surfaces/columns.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { Column } from '@/app/common/components/Base/List.tsx';
import { toggleOnEnter } from '@/app/common/filters/types/shared/toggleOnEnter.ts';
import { noop } from '@/app/components/search/Utilities.ts';
import {
  applyDefaultValueWhenNoSelected,
  multiSelectDataOf,
  optionIsDisabled,
  toggleValues,
} from '@/app/common/filters/core/filterOptionSelection.ts';

export const optionCheckboxId = (filterName: string, index: number) => `${filterName}_${index}`;

const Label = styled.div`
  ${({ theme }) => theme.kallisto.mixins.font('Title5')};
  display: block;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type FilterMultiSelectProps = FilterComponentProps & {
  customOptionCreators?: CustomOptionsCreators;
  // Slots a node between two options; undefined renders nothing.
  renderBetween?: (previous: FilterDataOption, option: FilterDataOption) => ReactNode;
  showCounter?: boolean;
  // An "Any" option is a reset control; the evo designs leave it out of the list.
  hideDefaultOption?: boolean;
};

export const FilterMultiSelect = ({
  filter,
  columns = 1,
  columnGap,
  isLoading,
  handleFilterChanged,
  customOptionCreators,
  renderBetween,
  showCounter = true,
  hideDefaultOption = false,
}: FilterMultiSelectProps) => {
  const filterData = multiSelectDataOf(filter);
  const { filtersWithRowLayout = [], useEvoFilters } = useFiltersUiData();
  const labelId = useId();
  // An option disabled under focus stays enabled, but inert, until focus moves on, so its focus is not dropped.
  const [focusedValue, setFocusedValue] = useState<string>();

  useEffect(() => {
    applyDefaultValueWhenNoSelected(filter, handleFilterChanged);
  }, [filter, handleFilterChanged]);

  const handleFilterClicked = (valueClicked: string) => handleFilterChanged([toggleValues(filter, [valueClicked])]);

  const shownOptions = hideDefaultOption
    ? filterData.options.filter((option) => !option.isDefault)
    : filterData.options;

  if (!shownOptions.length) return null;

  // The row gap is there for the circled-icon layout, so it follows the custom option creator.
  const rowGap = customOptionCreators?.[filter.name] ? 16 : 0;

  const options = shownOptions.flatMap((option, index) => {
    const isDisabled = optionIsDisabled({ option, filter, isLoading, useEvoFilters });
    const CustomComponent = customOptionCreators?.[filter.name];
    const between = index > 0 ? renderBetween?.(shownOptions[index - 1], option) : undefined;
    const withBetween = (row: ReactNode) => (between ? [between, row] : [row]);

    if (CustomComponent) {
      return withBetween(
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Enter bubbles up from the DS checkbox, which ignores it
        <li
          key={option.value}
          style={{ listStyle: 'none', minWidth: 0, minHeight: '32px', display: 'flex' }}
          onKeyDown={toggleOnEnter(isDisabled, () => handleFilterClicked(option.value))}
        >
          <CustomComponent option={option} onChange={handleFilterClicked} name={filter.name} />
        </li>
      );
    }

    return withBetween(
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Enter bubbles up from the DS checkbox, which ignores it
      <li
        key={option.value}
        style={{ listStyle: 'none', minWidth: 0, display: 'flex' }}
        onKeyDown={toggleOnEnter(isDisabled, () => handleFilterClicked(option.value))}
        onFocus={() => setFocusedValue(option.value)}
        onBlur={() => setFocusedValue(undefined)}
      >
        <TsxCheckbox
          key={option.value}
          $evo={useEvoFilters}
          checkbox={{
            id: optionCheckboxId(filter.name, index),
            label: <LabelNode option={option} isDisabled={isDisabled} showCounter={showCounter} />,
          }}
          className="checkbox"
          onChange={isDisabled ? noop : () => handleFilterClicked(option.value)}
          labelPosition="end"
          isChecked={option.selected}
          disabled={isDisabled && focusedValue !== option.value}
          fullWidth={true}
        />
      </li>
    );
  });

  return (
    <Container role={filterData.label ? 'group' : undefined} aria-labelledby={filterData.label ? labelId : undefined}>
      {filterData.label && <Label id={labelId}>{filterData.label}</Label>}
      <Column
        columns={columns}
        columnGap={columnGap}
        rows={getColumnRows(filter.name, options.length, columns, filtersWithRowLayout)}
        gap={rowGap}
        as="ul"
      >
        {options}
      </Column>
    </Container>
  );
};
